<script setup lang="ts">
import ContentView from './ContentView.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import DivisionsWidget from '@/components/widgets/divisions-widget/DivisionsWidget.vue'
import RadialBarChartTwo from '@/components/widgets/common/RadialBarChartTwo.vue'
import LineChart from '@/components/widgets/common/LineChart.vue'
import DbgHealthLogo from '@/components/icons/DbgHealthLogo.vue'
import ArrotexLogo from '@/components/icons/ArrotexLogo.vue'
import VidacorpLogo from '@/components/icons/VidacorpLogo.vue'
import AxeLogo from '@/components/icons/AxeLogo.vue'
import {ref, watch} from 'vue'
import {storeToRefs} from 'pinia'
import {useThemeStore, useValueDataStore} from '@/stores'
import {transformDataToRadialChartFormat, transformDbgDataToRadialChartFormat} from '@/components/common/common'
import ExportButton from '@/components/common/ExportButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'
import ErrorView from './ErrorView.vue'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'
import {RoleService} from "@/services/RoleService";

const themeStore = useThemeStore();
const { themeColors } = themeStore
const { isValueDataStoreLoading, valueData, isError } = storeToRefs(useValueDataStore());

const dbgTransformedData = ref(transformDbgDataToRadialChartFormat(valueData.value));

const roleService = new RoleService();

const arrotexTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.arrotex, 'arrotex')
)
const vidacorpTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.vidacorp, 'vidacorp')
)
const pharmaprogramsTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.pharmaprograms, 'axe')
)

watch(valueData, () => {
  dbgTransformedData.value = transformDbgDataToRadialChartFormat(valueData.value)
  arrotexTransformedData.value = transformDataToRadialChartFormat(
    valueData.value.arrotex,
    'arrotex'
  )
  vidacorpTransformedData.value = transformDataToRadialChartFormat(
    valueData.value.vidacorp,
    'vidacorp'
  )
  pharmaprogramsTransformedData.value = transformDataToRadialChartFormat(
    valueData.value.pharmaprograms,
    'axe'
  )
})
</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError" />
  <ContentView v-else>
    <template #top>
      <!-- <DataRefreshDatesButton/> -->
      <ExternalLinks />
      <SalesRepContactButton label="Contact your Arrotex Rep" type="arrotex" />
      <SalesRepContactButton label="Contact your VidaCorp Rep" type="vidacorp" />
      <ExportButton />
    </template>
    <template #middle>
      <DivisionsWidget
        :isMain="true"
        division="DBG"
        :totalValue="valueData.portfolioNetValue.dbgValue"
        :currentQuarterValue="valueData.portfolioNetValue.dbgValueCurrentQuarter"
        :opportunityValue="valueData.portfolioNetValue.dbgNetOpportunity"
        :glow="true"
      >
        <template #value-title>
          <DbgHealthLogo height="4.6875rem" width="7.8125rem" :fill="themeColors.colorBackgroundBright" />
        </template>
        <template #chart>
          <LineChart
            :data="valueData.portfolioNetValue.dbgTrend"
            :chartLegendValues="[
              { label: 'Arrotex', value: valueData.arrotex.value },
              { label: 'VidaCorp', value: valueData.vidacorp.value },
              {
                label: 'PharmaPrograms',
                value: valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValue, 0)
              }
            ]"
          />
        </template>
        <template #opportunity-title><span class="title">Net Opportunity</span></template>
      </DivisionsWidget>

      <DivisionsWidget
        :isMain="false"
        division="Arrotex"
        :totalValue="valueData.arrotex.value"
        :currentQuarterValue="valueData.arrotex.valueCurrentQuarter"
        :opportunityValue="valueData.arrotex.opportunity"
        v-if="roleService.hasRole('arrotex')"
        path="/benefits/arrotex"
      >
        <template #value-title>
          <ArrotexLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
        </template>
        <template #chart>
          <RadialBarChartTwo :data="arrotexTransformedData" :showLegend="true" />
        </template>
        <template #opportunity-title><span class="title">Arrotex Opportunity</span></template>
      </DivisionsWidget>

      <DivisionsWidget
        :isMain="false"
        division="VidaCorp"
        :totalValue="valueData.vidacorp.value"
        :currentQuarterValue="valueData.vidacorp.valueCurrentQuarter"
        :opportunityValue="valueData.vidacorp.opportunity"
        v-if="roleService.hasRole('vidacorp')"
        path="/benefits/vidacorp"
      >
        <template #value-title>
          <VidacorpLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
        </template>
        <template #chart>
          <RadialBarChartTwo :data="vidacorpTransformedData" :showLegend="true" />
        </template>
        <template #opportunity-title><span class="title">VidaCorp Opportunity</span></template>
      </DivisionsWidget>

      <DivisionsWidget
        :isMain="false"
        division="AXE"
        :totalValue="valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValue, 0)"
        :currentQuarterValue="
          valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValueCurrentQuarter, 0)
        "
        :opportunityValue="
          valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsOpportunityValue, 0)
        "
        v-if="roleService.hasRole('axe')"
        path="/benefits/axe"
      >
        <template #value-title>
          <AxeLogo width="4.6875rem" height="3.125rem" :fill="themeColors.colorText" />
        </template>
        <template #chart>
          <RadialBarChartTwo :data="pharmaprogramsTransformedData" :showLegend="true" />
        </template>
        <template #opportunity-title><span class="title">Axe Opportunity</span></template>
      </DivisionsWidget>
    </template>
  </ContentView>
</template>

<style scoped>
.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
}
</style>
